import React, { useEffect, useState } from "react";
import {
  setupEmailStatusIndicatorKind,
  type SetupEmailOperatorStatus,
} from "@/features/accounts/setupEmailDeliveryStatus";
import styles from "./SetupEmailStatusIndicator.module.scss";

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduced(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  return reduced;
}

type Props = {
  status: SetupEmailOperatorStatus | string | null | undefined;
};

const SetupEmailStatusIndicator: React.FC<Props> = ({ status }) => {
  const reducedMotion = usePrefersReducedMotion();
  const kind = setupEmailStatusIndicatorKind(status);

  let icon: React.ReactNode = null;
  let label = "";
  let animating = false;

  if (kind === "in_progress") {
    animating = !reducedMotion;
    label = reducedMotion
      ? "Delivery in progress"
      : "Waiting for the next delivery event";
    icon = reducedMotion ? (
      <i className={`bi bi-hourglass-split ${styles.icon}`} aria-hidden="true" />
    ) : (
      <span className={styles.spinner} aria-hidden="true" />
    );
  } else if (kind === "delivered") {
    label = "Delivered";
    icon = (
      <i className={`bi bi-check-circle-fill ${styles.success}`} aria-hidden="true" />
    );
  } else if (kind === "failed") {
    label = "Failed";
    icon = <i className={`bi bi-x-circle-fill ${styles.danger}`} aria-hidden="true" />;
  } else if (kind === "bounced") {
    label = "Bounced";
    icon = (
      <i
        className={`bi bi-exclamation-triangle-fill ${styles.warning}`}
        aria-hidden="true"
      />
    );
  }

  return (
    <span
      className={styles.slot}
      data-testid="setup-email-status-indicator"
      data-kind={kind}
      data-animating={animating ? "true" : "false"}
    >
      {icon}
      {label ? <span className="visually-hidden">{label}</span> : null}
    </span>
  );
};

export default SetupEmailStatusIndicator;
